import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react"
import CertIcon from "../ui/CertIcon"
import SubprocessorIcon from "../ui/SubprocessorIcon"
import { Icon, Check, Reveal } from "../ui/primitives"
import { useTC } from "../TrustCenterContext"
import { canDownloadDocuments } from "../../utils/documentAccess"

const PAGE_SIZE = 6

function DownloadButton({ loading, onClick }) {
    return (
        <button
            type="button"
            className={"btn btn--accent btn--sm" + (loading ? " btn--loading" : "")}
            onClick={onClick}
            disabled={loading}
            aria-busy={loading}
        >
            {loading ? (
                <>
                    <span className="btn__spinner" aria-hidden="true" />
                    Downloading…
                </>
            ) : (
                <>
                    <Icon name="download-tray" size={16} />
                    Download
                </>
            )}
        </button>
    )
}

function ViewButton({ onClick }) {
    return (
        <button type="button" className="btn btn--outline btn--sm" onClick={onClick}>
            <Icon name="file-check" size={16} />
            View
        </button>
    )
}

function ShowMoreButton({ total, expanded, onToggle, label }) {
    if (total <= PAGE_SIZE) return null
    const remaining = total - PAGE_SIZE
    return (
        <div className="section-show-more">
            <button type="button" className="btn btn--outline" onClick={onToggle}>
                {expanded ? "Show fewer" : `Show ${remaining} more ${label}`}
                <Icon
                    name="chevron-down"
                    size={16}
                    style={{
                        transition: "transform var(--motion-duration-base)",
                        transform: expanded ? "rotate(180deg)" : "none",
                    }}
                />
            </button>
        </div>
    )
}

export function SectionHead({ icon, eyebrow, title, lede }) {
    return (
        <Reveal className="section__head">
            <span className="eyebrow"><Icon name={icon} size={15} />{eyebrow}</span>
            <h2>{title}</h2>
            {lede && <p className="section__head-lede">{lede}</p>}
        </Reveal>
    )
}

function isTextClamped(el) {
    if (!el) return false
    return el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
}

function CertCard({ cert }) {
    const titleRef = useRef(null)
    const [titleOverflow, setTitleOverflow] = useState(false)
    const [titleHover, setTitleHover] = useState(false)

    const checkTitleOverflow = useCallback(() => {
        setTitleOverflow(isTextClamped(titleRef.current))
    }, [])

    useLayoutEffect(() => {
        const el = titleRef.current
        if (!el) return

        checkTitleOverflow()
        const raf = requestAnimationFrame(checkTitleOverflow)
        const delayed = setTimeout(checkTitleOverflow, 650)

        const observer = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(checkTitleOverflow)
            : null
        observer?.observe(el)
        window.addEventListener("resize", checkTitleOverflow)

        return () => {
            cancelAnimationFrame(raf)
            clearTimeout(delayed)
            observer?.disconnect()
            window.removeEventListener("resize", checkTitleOverflow)
        }
    }, [cert.short, checkTitleOverflow])

    const status = cert.status === "pending" || cert.status === "in_progress" || cert.status === "inactive"
        ? cert.status
        : null
    const statusLabel = status === "pending"
        ? "Pending"
        : status === "inactive"
            ? "Inactive"
            : status === "in_progress"
                ? "In progress"
                : null

    return (
        <div className={"card cert cert--tile" + (titleOverflow && titleHover ? " cert--title-tooltip" : "")}>
            <div className="cert-card__row">
                <span className="cert-card__ico"><CertIcon cert={cert} size={64} /></span>
                <div className="cat-card__meta">
                    <div className="cert__title-wrap">
                        <h3
                            ref={titleRef}
                            className="card-title cert__truncate cert__truncate--1"
                            onMouseEnter={() => {
                                checkTitleOverflow()
                                setTitleHover(true)
                            }}
                            onMouseLeave={() => setTitleHover(false)}
                        >
                            {cert.short}
                        </h3>
                        {titleOverflow && titleHover && (
                            <div className="cert__hover-detail" role="tooltip">
                                <strong>{cert.short}</strong>
                            </div>
                        )}
                    </div>
                    {cert.body && <div className="cat-card__sub cert__truncate cert__truncate--1">{cert.body}</div>}
                </div>
                {status && (
                    <span className={`cert__status cert__status--${status}`}>
                        <span className="dot" />{statusLabel}
                    </span>
                )}
            </div>
            {cert.desc && <p className="cat-card__blurb cert__truncate cert__truncate--2">{cert.desc}</p>}
        </div>
    )
}

export function CertificationsSection() {
    const D = useTC()
    const [showAll, setShowAll] = useState(false)
    const firstRun = useRef(true)
    const shown = showAll ? D.certs : D.certs.slice(0, PAGE_SIZE)

    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false
            return
        }
        if (!showAll) {
            const el = document.getElementById("certifications")
            if (el) {
                requestAnimationFrame(() => {
                    window.scrollTo({
                        top: el.getBoundingClientRect().top + window.scrollY - 80,
                        behavior: "smooth",
                    })
                })
            }
        }
    }, [showAll])

    if (!D.certs.length) return null

    return (
        <section className="section section--page" id="certifications">
            <div className="shell">
                <SectionHead
                    icon="ribbon-star"
                    eyebrow="Certifications & attestations"
                    title="Compliance at a glance"
                    lede="Audited and certified by recognised bodies — renewed on a regular cadence so what you see here stays current."
                />
                <div className="grid grid--3 grid--cert">
                    {shown.map((c, i) => (
                        <Reveal key={c.id} delay={Math.min(i, 5) * 60} className="grid__cell">
                            <CertCard cert={c} />
                        </Reveal>
                    ))}
                </div>
                {D.certs.length > PAGE_SIZE && (
                    <ShowMoreButton
                        total={D.certs.length}
                        expanded={showAll}
                        onToggle={() => setShowAll((v) => !v)}
                        label="certifications"
                    />
                )}
            </div>
        </section>
    )
}

function ControlAccordion({ cat, open, onToggle }) {
    const [showAllControls, setShowAllControls] = useState(false)
    const visibleControls = showAllControls ? cat.controls : cat.controls.slice(0, PAGE_SIZE)

    useEffect(() => {
        if (!open) setShowAllControls(false)
    }, [open])

    return (
        <div className={"ctrl" + (open ? " open" : "")}>
            <div className="ctrl__head" onClick={onToggle}>
                <span className="ctrl__ico"><Icon name={cat.icon} size={20} /></span>
                <div className="ctrl__meta">
                    <h3>{cat.name}</h3>
                    {cat.blurb && <p title={cat.blurb}>{cat.blurb}</p>}
                </div>
                <span className="ctrl__n">{cat.controls.length}</span>
                <Icon name="chevron-down" size={20} className="ctrl__chev" />
            </div>
            <div className="ctrl__body">
                <div>
                    <div className="ctrl__list">
                        {visibleControls.map((ct) => (
                            <div className="ctrl__item" key={ct.name}>
                                <span className="ctrl__check"><Check size={13} /></span>
                                <div>
                                    <h4>{ct.name}</h4>
                                    <p>{ct.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    {cat.controls.length > PAGE_SIZE && (
                        <div className="ctrl__show-more">
                            <button
                                type="button"
                                className="btn btn--outline btn--sm"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setShowAllControls((v) => !v)
                                }}
                            >
                                {showAllControls
                                    ? "Show fewer controls"
                                    : `Show ${cat.controls.length - PAGE_SIZE} more controls`}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export function ControlsSection() {
    const D = useTC()
    const [showAllCategories, setShowAllCategories] = useState(false)
    const [open, setOpen] = useState(() => {
        const init = {}
        D.categories.forEach((c, i) => { init[c.id] = i === 0 })
        return init
    })

    const visibleCategories = showAllCategories ? D.categories : D.categories.slice(0, PAGE_SIZE)

    return (
        <section className="section section--page section--white" id="controls">
            <div className="shell">
                <SectionHead
                    icon="padlock-locked"
                    eyebrow="Security controls"
                    title="Every control, transparently documented"
                    lede="The technical and organizational measures that keep your people data safe — grouped, audited and kept current."
                />
                <div className="ctrl-group">
                    {visibleCategories.map((c, i) => (
                        <Reveal key={c.id} delay={i * 40}>
                            <ControlAccordion
                                cat={c}
                                open={!!open[c.id]}
                                onToggle={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}
                            />
                        </Reveal>
                    ))}
                </div>
                <ShowMoreButton
                    total={D.categories.length}
                    expanded={showAllCategories}
                    onToggle={() => setShowAllCategories((v) => !v)}
                    label="categories"
                />
            </div>
        </section>
    )
}

export function DocumentsSection({ user, myRequests = [], onRequestAccess, onDownload, onView, onLoginRequired, downloadingDocId = null }) {
    const D = useTC()
    const [filter, setFilter] = useState("All")
    const [showAll, setShowAll] = useState(false)
    const cats = ["All", ...Array.from(new Set(D.documents.map((d) => d.cat)))]
    const list = D.documents.filter((d) => filter === "All" || d.cat === filter)
    const visibleList = showAll ? list : list.slice(0, PAGE_SIZE)

    useEffect(() => {
        setShowAll(false)
    }, [filter])

    const isPendingRequest = (docId) =>
        myRequests.some((r) => String(r.documentId) === String(docId) && r.status === "pending")

    const canDownload = canDownloadDocuments(user)

    const hasAccess = (doc) => {
        if (!user) return false
        if (!doc.gated) return true
        if (user.role === "admin") return true
        if (user.accessibleDocs?.some((id) => String(id) === String(doc.id))) return true
        return myRequests.some(
            (r) => String(r.documentId) === String(doc.id) && r.status === "approved"
        )
    }

    const handleAction = (doc) => {
        if (downloadingDocId) return
        if (!user) {
            onLoginRequired?.(doc._apiDoc)
            return
        }
        if (hasAccess(doc)) {
            onDownload?.(doc._apiDoc)
        } else {
            onRequestAccess?.(doc._apiDoc)
        }
    }

    const handleView = (doc) => {
        if (!user) {
            onLoginRequired?.(doc._apiDoc)
            return
        }
        if (hasAccess(doc)) {
            onView?.(doc._apiDoc)
        } else {
            onRequestAccess?.(doc._apiDoc)
        }
    }

    return (
        <section className="section section--page" id="documents">
            <div className="shell">
                <SectionHead
                    icon="file-shield"
                    eyebrow="Document library"
                    title="Reports, certificates & policies"
                    lede="Sign in to access documents. Customers and admins can view and download. Prospects can always view; download depends on your organization's access policy."
                />
                <Reveal className="doc-filters">
                    {cats.map((c) => (
                        <button
                            key={c}
                            type="button"
                            className={"btn btn--sm " + (filter === c ? "btn--primary" : "btn--outline")}
                            onClick={() => setFilter(c)}
                        >
                            {c}
                        </button>
                    ))}
                </Reveal>
                <div className="grid" style={{ gap: 12 }}>
                    {visibleList.map((d, i) => {
                        const docHasAccess = hasAccess(d)
                        const isAdmin = user?.role === "admin"
                        const isApproved = myRequests.some(
                            (r) => String(r.documentId) === String(d.id) && r.status === "approved"
                        )
                        
                        const getAccessBadge = () => {
                            if (!d.gated) {
                                return <span className="tag-pill tag-pill--open"><Icon name="download-tray" size={12} />Open</span>
                            }
                            if (isAdmin) {
                                return null
                            }
                            if (docHasAccess || isApproved) {
                                return <span className="tag-pill tag-pill--granted"><Icon name="file-check" size={12} />Access granted</span>
                            }
                            if (user?.ndaSigned) {
                                if (isPendingRequest(d.id)) {
                                    return <span className="tag-pill tag-pill--pending"><Icon name="clock" size={12} />Pending approval</span>
                                }
                                return <span className="tag-pill tag-pill--pending"><Icon name="file-shield" size={12} />Request access</span>
                            }
                            return <span className="tag-pill tag-pill--gated"><Icon name="padlock-locked" size={12} />NDA required</span>
                        }
                        
                        return (
                        <Reveal key={d.id} delay={i * 40}>
                            <div className="doc">
                                <span className="doc__ico"><Icon name={d.icon} size={24} /></span>
                                <div className="doc__meta">
                                    <h3>{d.name}</h3>
                                    <div className="doc__tags">
                                        <span className="tag-pill tag-pill--cat">{d.cat}</span>
                                        {getAccessBadge()}
                                    </div>
                                </div>
                                <div className="doc__actions">
                                    {hasAccess(d)
                                        ? (
                                            <>
                                                <ViewButton onClick={() => handleView(d)} />
                                                {canDownload && (
                                                    <DownloadButton loading={downloadingDocId === d.id} onClick={() => handleAction(d)} />
                                                )}
                                            </>
                                        )
                                        : !user && !d.gated
                                            ? (
                                                <>
                                                    <ViewButton onClick={() => onLoginRequired?.(d._apiDoc)} />
                                                    <button type="button" className="btn btn--accent btn--sm" onClick={() => onLoginRequired?.(d._apiDoc)}>
                                                        <Icon name="download-tray" size={16} />
                                                        Download
                                                    </button>
                                                </>
                                            )
                                        : isPendingRequest(d.id)
                                            ? <span className="statusline" style={{ background: "var(--gl-blue-50)", borderColor: "var(--gl-blue-100)", color: "var(--gl-blue-700)" }}><Icon name="paper-plane" size={12} />Requested</span>
                                            : <button type="button" className="btn btn--outline btn--sm" onClick={() => handleAction(d)}><Icon name="key" size={16} />Request access</button>}
                                </div>
                            </div>
                        </Reveal>
                        )
                    })}
                    {!list.length && (
                        <p style={{ color: "var(--text-neutral-secondary)", textAlign: "center", padding: "40px 0" }}>
                            No documents available in this category.
                        </p>
                    )}
                </div>
                <ShowMoreButton
                    total={list.length}
                    expanded={showAll}
                    onToggle={() => setShowAll((v) => !v)}
                    label={filter === "All" ? "documents" : `${filter.toLowerCase()} documents`}
                />
            </div>
        </section>
    )
}

export function SubprocessorsSection() {
    const D = useTC()
    const [showAll, setShowAll] = useState(false)
    if (!D.subprocessors?.length) return null

    const visible = showAll ? D.subprocessors : D.subprocessors.slice(0, PAGE_SIZE)

    return (
        <section className="section section--page section--white" id="subprocessors">
            <div className="shell">
                <SectionHead
                    icon="person-group"
                    eyebrow="Sub-processors"
                    title="Who we work with"
                    lede="The third parties that help us deliver the service. We assess each one and notify customers of material changes."
                />
                <Reveal>
                    <table className="tbl">
                        <thead><tr><th>Sub-processor</th><th>Purpose</th><th>Location</th></tr></thead>
                        <tbody>
                            {visible.map((s) => (
                                <tr key={s.name}>
                                    <td>
                                        {s.logoUrl ? (
                                            <img src={s.logoUrl} alt="" className="sp-icon__img sp-icon__img--direct" />
                                        ) : (
                                            <span className="sp-logo"><SubprocessorIcon item={s} size={20} /></span>
                                        )}
                                        <span className="sp-name">{s.name}</span>
                                    </td>
                                    <td style={{ color: "var(--text-neutral-secondary)" }}>{s.purpose}</td>
                                    <td style={{ color: "var(--text-neutral-secondary)" }}>{s.location}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Reveal>
                <ShowMoreButton
                    total={D.subprocessors.length}
                    expanded={showAll}
                    onToggle={() => setShowAll((v) => !v)}
                    label="sub-processors"
                />
            </div>
        </section>
    )
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false)
    return (
        <div className={"ctrl" + (open ? " open" : "")}>
            <div className="ctrl__head" onClick={() => setOpen(!open)}>
                <div className="ctrl__meta"><h3 style={{ fontSize: "var(--font-size-body-l)" }}>{q}</h3></div>
                <Icon name="chevron-down" size={20} className="ctrl__chev" />
            </div>
            <div className="ctrl__body">
                <div>
                    <div style={{ padding: "0 20px 20px 20px", color: "var(--text-neutral-secondary)", fontSize: "var(--font-size-body-m)", lineHeight: 1.6 }}>
                        {a}
                    </div>
                </div>
            </div>
        </div>
    )
}

export function FaqSection() {
    const D = useTC()
    const [showAll, setShowAll] = useState(false)
    if (!D.faqs?.length) return null

    const visibleFaqs = showAll ? D.faqs : D.faqs.slice(0, PAGE_SIZE)

    return (
        <section className="section section--page section--page-bottom" id="faq">
            <div className="shell faq">
                <SectionHead
                    icon="question-mark"
                    eyebrow="FAQ"
                    title="Common questions"
                    lede="Quick answers to what enterprise security and procurement teams ask us most."
                />
                {visibleFaqs.map((f, i) => (
                    <Reveal key={i} delay={i * 40} style={{ marginBottom: 12 }}>
                        <FaqItem q={f.q} a={f.a} />
                    </Reveal>
                ))}
                <ShowMoreButton
                    total={D.faqs.length}
                    expanded={showAll}
                    onToggle={() => setShowAll((v) => !v)}
                    label="questions"
                />
            </div>
        </section>
    )
}
